import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import OrderKittingService from 'services/OrderKittingService';
import {
  ADMIN_HANDLES,
  KittingAction,
  money,
  OrderDetail,
  OUTCOMES,
  parseRemovedComponents,
} from '../types';

const OUTCOME_COLOR: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
  [OUTCOMES.WOULD_KIT]: 'info',
  [OUTCOMES.KITTED]: 'success',
  [OUTCOMES.SKIPPED]: 'warning',
  [OUTCOMES.ERROR]: 'error',
};

function LabelValue({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} py={0.5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {value ?? <span style={{ color: '#9e9e9e' }}>None</span>}
      </Typography>
    </Box>
  );
}

// Everything about one logged kitting action: what the rule decided (from the
// log row — no refetch, so it is exactly what was recorded) alongside every
// line item on the order, fetched live. Seeing the whole order is the point:
// a row alone cannot tell you whether the rule grabbed the right items or what
// else the customer bought.
export default function OrderDetailDrawer({
  action,
  open,
  onClose,
}: {
  action: KittingAction | null;
  open: boolean;
  onClose: () => void;
}) {
  const service = React.useMemo(() => new OrderKittingService(), []);
  const [detail, setDetail] = React.useState<OrderDetail | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);

  // Fetch keyed on the identity of the action, not the object, so a re-render
  // of the parent cannot re-trigger the request.
  const store = action?.store ?? '';
  const orderId = action?.order_id ?? 0;

  const load = React.useCallback(async () => {
    if (!store || !orderId) return;
    setLoading(true);
    setError(false);
    setDetail(null);
    const result = await service.getOrderDetail(store, orderId);
    if (result) setDetail(result.order);
    else setError(true);
    setLoading(false);
  }, [service, store, orderId]);

  React.useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  if (!action) return null;

  const removed = parseRemovedComponents(action.components_removed);
  const removedIds = new Set(removed.map(c => c.lineItemId).filter(Boolean));
  const removedSkus = new Set(removed.map(c => c.sku));
  const totalsMatch =
    action.order_total_after == null || action.order_total_after === action.order_total_before;

  // A line the rule consumed. lineItemId is exact; older rows fall back to SKU,
  // which can over-match when a SKU repeats — acceptable for a visual hint.
  const wasConsumed = (lineItemId: number, sku: string | null) =>
    removedIds.size > 0 ? removedIds.has(lineItemId) : sku != null && removedSkus.has(sku);

  const isKitLine = (sku: string | null) => action.kit_sku != null && sku === action.kit_sku;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: 1300 }}>
      <Box sx={{ width: 560, p: 3, position: 'relative' }}>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>

        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            Order {action.order_name}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              {dayjs(action.order_created_at).format('MMM D, YYYY HH:mm')}
            </Typography>
            <Chip label={action.store} size="small" variant="outlined" />
            <Chip
              label={action.outcome}
              size="small"
              color={OUTCOME_COLOR[action.outcome] ?? 'default'}
            />
            <Chip
              label={action.mode === 'live' ? 'LIVE' : 'dry-run'}
              size="small"
              color={action.mode === 'live' ? 'error' : 'default'}
            />
          </Stack>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewIcon />}
          href={`https://admin.shopify.com/store/${ADMIN_HANDLES[action.store] ?? 'dosaze'}/orders/${action.order_id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mb: 2 }}
          fullWidth
        >
          View in Shopify
        </Button>

        <Divider sx={{ mb: 2 }} />

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Kitting
            </Typography>

            {removed.length > 0 ? (
              <Box sx={{ mb: 1 }}>
                {removed.map((component, index) => (
                  <Typography
                    key={`${component.sku}-${index}`}
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: 12 }}
                  >
                    −{component.fromQty - component.toQty} × {component.sku}
                    {component.toQty > 0 && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {'  '}({component.fromQty} → {component.toQty} left)
                      </Typography>
                    )}
                  </Typography>
                ))}
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, mt: 0.5 }}>
                  +{action.times ?? 1} × {action.kit_sku ?? action.kit_variant_id}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Nothing was substituted on this order.
              </Typography>
            )}

            <Divider sx={{ my: 1 }} />

            {/* The three money figures are one equation — kit list minus the
                discount is what was paid — so they read as one line rather
                than four label/value rows repeating the same number. */}
            {action.paid_amount != null && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {money(action.paid_amount, action.store)} paid
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' → kit '}
                  {money(action.kit_list_price, action.store)}
                  {action.discount_applied ? ` less ${money(action.discount_applied, action.store)}` : ''}
                </Box>
              </Typography>
            )}
            {action.order_total_before != null && (
              <Typography variant="body2" color={totalsMatch ? 'success.main' : 'error'}>
                {totalsMatch
                  ? `Order total unchanged at ${money(action.order_total_before, action.store)}`
                  : `Order total moved ${money(action.order_total_before, action.store)} → ${money(
                      action.order_total_after,
                      action.store,
                    )}`}
              </Typography>
            )}

            <Divider sx={{ my: 1 }} />

            <LabelValue label="Rule" value={action.rule_id ?? '—'} />
            {action.rule_description && (
              <LabelValue label="Description" value={action.rule_description} />
            )}
            <LabelValue
              label="Evaluated"
              value={dayjs(action.created_at).format('MMM D, YYYY HH:mm')}
            />
            {action.reason && (
              <Alert severity={action.outcome === OUTCOMES.ERROR ? 'error' : 'info'} sx={{ mt: 1 }}>
                {action.reason}
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            {/* The heading carries the then/now distinction; the alert fires
                only for the case that reads as a contradiction in the table —
                a would_kit row whose order is fulfilled now. */}
            <Typography variant="subtitle2" gutterBottom>
              Order contents — live from Shopify, as it is now
            </Typography>
            {detail &&
              !loading &&
              action.outcome === OUTCOMES.WOULD_KIT &&
              !action.reason &&
              detail.fulfillmentStatus !== 'UNFULFILLED' && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Unfulfilled when this ran, fulfilled since — the 60-minute ShipBob
                  import delay is what keeps that window open.
                </Alert>
              )}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {error && (
              <Alert
                severity="error"
                sx={{ mt: 1 }}
                action={
                  <Button size="small" onClick={load}>
                    Retry
                  </Button>
                }
              >
                Failed to load this order from Shopify.
              </Alert>
            )}

            {detail && !loading && (
              <>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Paid</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.lineItems.map(line => {
                      const consumed = wasConsumed(line.lineItemId, line.sku);
                      const kit = isKitLine(line.sku);
                      const removedNow = line.currentQuantity === 0;
                      return (
                        <TableRow
                          key={line.lineItemId}
                          sx={removedNow ? { opacity: 0.6 } : {}}
                        >
                          <TableCell>
                            <Typography variant="body2">{line.name ?? line.sku ?? '—'}</Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace', fontSize: 11 }}
                            >
                              {line.sku ?? 'no SKU'}
                            </Typography>
                            {(consumed || kit) && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  size="small"
                                  color={kit ? 'success' : 'info'}
                                  variant={kit ? 'filled' : 'outlined'}
                                  label={kit ? 'kit' : 'replaced by the kit'}
                                />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {line.currentQuantity === line.quantity
                                ? line.quantity
                                : `${line.quantity} → ${line.currentQuantity}`}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {money(line.paidAmount, action.store)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {detail.lineItemsTruncated && (
                  <Typography variant="caption" color="text.secondary">
                    Only the first 50 line items are shown.
                  </Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <LabelValue
                  label="Order total"
                  value={money(detail.totalPrice, action.store)}
                />
                <LabelValue label="Payment" value={detail.financialStatus} />
                <LabelValue label="Fulfillment" value={detail.fulfillmentStatus} />
                {detail.tags.length > 0 && (
                  <LabelValue label="Tags" value={detail.tags.join(', ')} />
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Drawer>
  );
}
